import { useEffect, useRef, useState } from "react";

import { api, ApiError } from "../api";
import { t } from "../i18n";
import type { Datapoint, KycSession, Locale, OnboardingState } from "../types";
import { reportError } from "../lib/ybug";

interface Props {
  recordId: string;
  datapoint: Datapoint;
  locale: Locale;
  onDone: (state: OnboardingState) => void;
  onFallback: (message: string) => void;
}

type Phase = "idle" | "starting" | "running" | "finishing" | "failed";

const DOCUMENT_BY_KEY: Record<string, string> = {
  idenfy_dowod_osobisty: "ID_CARD",
  idenfy_karta_pobytu: "RESIDENCE_PERMIT",
  idenfy_wiza: "VISA",
};

/** Co ile pytamy backend o wynik weryfikacji. */
const POLL_MS = 4000;

/**
 * Weryfikacja dostawcy w ramce na tej samej stronie. Kiedy dostawca nie odpowiada,
 * nie zostawiamy pustego ekranu z kręcącym się kółkiem — proces przechodzi
 * na ręczne wgranie zdjęć dokumentu.
 *
 * Wynik weryfikacji wraca do nas webhookiem, o którym przeglądarka nie ma jak
 * się dowiedzieć. Dlatego w trybie live odpytujemy backend: bez tego iframe
 * zostawał na ekranie także wtedy, gdy proces był już dawno rozstrzygnięty.
 */
export function KycWidget({ recordId, datapoint, locale, onDone, onFallback }: Props) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [session, setSession] = useState<KycSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  // onDone przychodzi z App jako świeża domknięcie przy każdym renderze — trzymamy
  // je w ref, żeby nie restartować odpytywania po każdej zmianie stanu rodzica.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    setPhase("idle");
    setSession(null);
    setError(null);
  }, [datapoint.key]);

  const start = async () => {
    setPhase("starting");
    setError(null);
    try {
      const created = await api.startKyc(recordId, datapoint.key, locale);
      setSession(created);
      setPhase("running");
    } catch (caught) {
      const failure = caught as ApiError;
      setPhase("idle");
      setError(failure.message);
      if (failure.body?.fallback === "upload") onFallback(failure.message);
    }
  };

  useEffect(() => {
    // W trybie demonstracyjnym wynik domyka przycisk, nie webhook.
    if (phase !== "running" || !session || session.mock) return;

    let alive = true;
    const timer = window.setInterval(async () => {
      let result;
      try {
        result = await api.kycStatus(recordId, datapoint.key, locale);
      } catch {
        return; // chwilowy brak sieci nie może przerwać weryfikacji
      }
      if (!alive) return;

      if (result.status === "approved" && result.state) {
        onDoneRef.current(result.state);
      } else if (result.status === "denied") {
        reportError("kyc_session", { code: result.reason ?? "denied", path: `/kyc/${datapoint.key}` });
        setPhase("failed");
        setError(t(result.reason === "wrong_document" ? "kycWrongDocument" : "kycDenied", locale));
      } else if (result.status === "expired") {
        reportError("kyc_session", { code: "expired", path: `/kyc/${datapoint.key}` });
        setPhase("failed");
        setError(t("kycExpired", locale));
      }
    }, POLL_MS);

    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [phase, session, recordId, datapoint.key, locale]);

  // Na telefonie ramka dostawcy zajmuje cały ekran (patrz `.kyc--live` w CSS),
  // więc strona pod spodem nie ma się prawa przewijać: człowiek robi zdjęcie
  // twarzy, a każdy przypadkowy ruch palcem psuł ujęcie i przesuwał kadr.
  const naZywo = phase === "running" && Boolean(session) && !session?.mock;
  useEffect(() => {
    if (!naZywo) return;
    document.body.classList.add("kyc-open");
    return () => document.body.classList.remove("kyc-open");
  }, [naZywo]);

  const finishMock = async () => {
    if (!session) return;
    setPhase("finishing");
    try {
      const state = await api.completeKycMock(
        recordId,
        datapoint.key,
        session.session_id,
        DOCUMENT_BY_KEY[datapoint.key] ?? "ID_CARD",
      );
      onDone(state);
    } catch (caught) {
      setPhase("running");
      setError((caught as ApiError).message);
    }
  };

  if (phase === "idle" || phase === "failed") {
    return (
      <>
        {datapoint.hint && <p className="field__hint">{datapoint.hint}</p>}
        {error && <p className="error">{error}</p>}
        <div className="actions">
          <button className="btn btn--primary btn--wide" onClick={start}>
            {phase === "failed" ? t("kycRetry", locale) : t("startKyc", locale)}
          </button>
        </div>
        {phase === "failed" && (
          <div className="actions">
            <button
              className="btn--link"
              onClick={() => onFallback(error ?? t("kycExpired", locale))}
            >
              {t("kycUploadInstead", locale)}
            </button>
          </div>
        )}
      </>
    );
  }

  if (phase === "starting") {
    // Szkielet zamiast samego kółka: widać, że coś się buduje, a nie że zawisło.
    return (
      <div className="kyc">
        <div className="kyc__bar">
          <span className="kyc__dot" />
          iDenfy
        </div>
        <div className="kyc__body">
          <div className="skeleton" style={{ maxWidth: 220 }} />
          <div className="skeleton" style={{ maxWidth: 300, height: 120, borderRadius: 12 }} />
          <div className="skeleton" style={{ maxWidth: 160 }} />
        </div>
      </div>
    );
  }

  /**
   * Adres sesji u dostawcy z językiem w parametrze.
   *
   * W ramce liczy się wyłącznie `lang` z adresu — `locale` podane przy
   * tworzeniu tokenu nie robi tam nic (tak mówi dokumentacja dostawcy i tak
   * zachowuje się jego ekran). Dlatego kandydat oglądał polski formularz mimo
   * ukraińskiego interfejsu.
   *
   * Przełączenie języka przeładowuje ramkę pod tym samym tokenem: to ta sama
   * sesja, tylko w innym języku — nikt nie traci rozpoczętego skanowania.
   */
  const adresDostawcy = session?.redirect_url
    ? `${session.redirect_url}${session.redirect_url.includes("?") ? "&" : "?"}lang=${locale}`
    : "";

  return (
    <>
      <div className={`kyc${session?.mock ? "" : " kyc--live"}`}>
        <div className="kyc__bar">
          <span className="kyc__dot" />
          <span>{session?.provider ?? "iDenfy"}</span>
          <span style={{ marginLeft: "auto" }}>{session?.mock ? "demo" : "live"}</span>
          {/* Wyjście awaryjne musi być w tym samym pudełku co ramka: na telefonie
              zajmuje ona cały ekran, a link zostawiony pod spodem byłby niewidoczny
              dokładnie wtedy, gdy jest potrzebny — czyli gdy dostawca się nie wyświetlił. */}
          {!session?.mock && (
            <a className="kyc__escape" href={adresDostawcy}
               target="_blank" rel="noreferrer">
              {t("kycOpenInTab", locale)}
            </a>
          )}
        </div>

        {session?.mock ? (
          <div className="kyc__body">
            <p className="field__hint" style={{ margin: 0 }}>
              {t("kycMockNote", locale)}
            </p>
            <button
              className={`btn btn--primary${phase === "finishing" ? " puls" : ""}`}
              onClick={finishMock}
              disabled={phase === "finishing"}
            >
              {phase === "finishing" ? t("saving", locale) : t("kycSimulate", locale)}
            </button>
          </div>
        ) : (
          <iframe
            // Klucz z sesji i języka: przełączenie języka ma przeładować ramkę,
            // a ponowienie — zamontować świeżą, a nie reanimować tę, w której
            // dostawca zdążył już zerwać połączenie.
            key={`${session?.session_id}-${locale}`}
            className="kyc__frame"
            src={adresDostawcy}
            title={datapoint.title}
            allow="camera; microphone; fullscreen"
          />
        )}
      </div>

      <p className="field__hint">{t("kycRunning", locale)}</p>
      {error && <p className="error">{error}</p>}
    </>
  );
}
